<template>
  <div class="wizard-wrapper animate-fade-in">
    <div class="glass-card wizard-box">
      
      <div class="progress-bar">
        <div class="progress-fill" :style="{ width: progressPercentage + '%' }"></div>
      </div>

      <transition name="slide-step" mode="out-in">
        
        <div v-if="step === 1" key="step1" class="step-container">
          <h2 class="step-title">What is your main goal right now?</h2>
          <p class="step-sub">Select the biggest challenge you want to solve.</p>
          
          <div class="options-grid">
            <button @click="selectGoal('Get more new clients')" class="option-card">
              <div class="text-blue-400 option-icon"><i class="fa fa-users"></i></div>
              <span class="option-text">Get more new clients</span>
            </button>
            <button @click="selectGoal('Stop no-shows & cancellations')" class="option-card">
              <div class="text-red-400 option-icon"><i class="fa fa-calendar-times"></i></div>
              <span class="option-text">Stop no-shows & cancellations</span>
            </button>
            <button @click="selectGoal('Save time on admin & booking')" class="option-card">
              <div class="option-icon text-emerald-400"><i class="fa fa-clock"></i></div>
              <span class="option-text">Save time on admin & booking</span>
            </button>
            <button @click="selectGoal('Scale to multiple barbers/locations')" class="option-card">
              <div class="text-purple-400 option-icon"><i class="fa fa-store"></i></div>
              <span class="option-text">Scale to multiple barbers/locations</span>
            </button>
          </div>
        </div>

        <div v-else-if="step === 2" key="step2" class="step-container">
          <button @click="step = 1" class="mb-4 text-back-btn"><i class="fa fa-arrow-left"></i> Back</button>
          
          <h2 class="step-title">How many barbers are in your shop?</h2>
          <p class="step-sub">This helps us tailor the setup to your specific needs.</p>
          
          <div class="options-grid list-style">
            <button @click="selectSize('Just me (Solo Barber)')" class="option-card-row">
              <span class="option-text">Just me (Solo Barber)</span>
              <i class="fa fa-chevron-right arrow"></i>
            </button>
            <button @click="selectSize('2 to 4 Barbers')" class="option-card-row">
              <span class="option-text">2 to 4 Barbers</span>
              <i class="fa fa-chevron-right arrow"></i>
            </button>
            <button @click="selectSize('5+ Barbers')" class="option-card-row">
              <span class="option-text">5+ Barbers</span>
              <i class="fa fa-chevron-right arrow"></i>
            </button>
          </div>
        </div>

        <div v-else-if="step === 3" key="step3" class="step-container">
          <button @click="step = 2" class="mb-4 text-back-btn"><i class="fa fa-arrow-left"></i> Back</button>
          
          <h2 class="step-title">Perfect. Where should we send your custom setup?</h2>
          <p class="step-sub">Drop your email or phone number below.</p>
          
          <div class="mt-2 input-with-icon">
            <i class="fa fa-paper-plane icon"></i>
            <input 
              v-model="form.contact" 
              type="text" 
              class="pl-10 text-lg input-glass" 
              placeholder="Email or phone number" 
              @keyup.enter="submitLead"
            />
          </div>
          
          <button 
            @click="submitLead" 
            :disabled="!form.contact.trim() || isSubmitting" 
            class="justify-center w-full py-3 mt-6 text-lg dash-btn btn-primary-solid"
          >
            <i :class="isSubmitting ? 'fa fa-circle-notch fa-spin' : 'fa fa-paper-plane'"></i>
            {{ isSubmitting ? 'Sending...' : 'Get My Custom Offer' }}
          </button>
          <p class="mt-3 text-center privacy-note"><i class="fa fa-lock"></i> No spam. Fast setup.</p>
        </div>

        <div v-else key="step4" class="py-6 text-center step-container">
          <div class="mb-4 success-icon"><i class="fa fa-check"></i></div>
          <h2 class="step-title">We got it!</h2>
          <p class="mb-0 step-sub">Your request has been sent directly to our team. We'll be in touch with your custom offer shortly.</p>
          <button @click="resetForm" class="mx-auto mt-8 dash-btn btn-ghost border-visible">
            Send another request
          </button>
        </div>

      </transition>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ContactView',
  data() {
    return {
      step: 1,
      isSubmitting: false,
      form: {
        goal: '',
        size: '',
        contact: ''
      }
    }
  },
  computed: {
    progressPercentage() {
      if (this.step === 1) return 25;
      if (this.step === 2) return 50;
      if (this.step === 3) return 85;
      return 100;
    }
  },
  methods: {
    selectGoal(goal) {
      this.form.goal = goal;
      setTimeout(() => { this.step = 2; }, 150);
    },
    selectSize(size) {
      this.form.size = size;
      setTimeout(() => { this.step = 3; }, 150);
    },
    async submitLead() {
      if (!this.form.contact.trim()) return;
      
      this.isSubmitting = true;
      
      try {
        // We use FormSubmit's AJAX endpoint so the page doesn't refresh!
        // IMPORTANT: Replace the email below with your real email address
        await axios.post('https://formsubmit.co/ajax/5238613deccce5a9be7e0711ae92609e', {
          _subject: "🚨 New Barbers Unity Lead!",
          Goal: this.form.goal,
          Shop_Size: this.form.size,
          Contact_Info: this.form.contact,
          _template: "table" // This makes the email arrive in a clean, easy-to-read table
        });
        
        this.step = 4; // Move to the success screen
      } catch (error) {
        console.error("Failed to send lead:", error);
        // Even if it fails, you might want to show an error toast here
      } finally {
        this.isSubmitting = false;
      }
    },
    resetForm() {
      this.form = { goal: '', size: '', contact: '' };
      this.step = 1;
    }
  }
}
</script>

<style scoped>
/* ── DESIGN TOKENS ─────────────────────────────────────── */
.wizard-wrapper {
  --accent:        #4a7fa5;
  --accent-light:  #6499bb;
  --accent-glow:   rgba(74, 127, 165, 0.25);
  --surface:       rgba(15, 20, 32, 0.65);
  --border:        rgba(255, 255, 255, 0.08);
  --text-primary:  #f0f4f8;
  --text-secondary:#94a3b8;
  --text-muted:    #64748b;
  
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh; /* Changed from calc to 100vh to utilize top padding properly */
  padding: 8rem 1rem 4rem; /* ADDED PROPER TOP PADDING TO CLEAR NAVBAR */
  color: var(--text-primary);
}

/* ── GLASS CARD ────────────────────────────────────────── */
.glass-card { 
  background: var(--surface); 
  border: 1px solid var(--border); 
  border-radius: 24px; 
  backdrop-filter: blur(24px); 
  -webkit-backdrop-filter: blur(24px); 
  box-shadow: 0 24px 60px rgba(0,0,0,0.4), 0 0 0 1px rgba(255, 255, 255, 0.02); 
  overflow: hidden; 
}
.wizard-box {
  width: 100%;
  max-width: 540px;
  position: relative;
}

/* ── PROGRESS BAR ──────────────────────────────────────── */
.progress-bar {
  height: 4px;
  background: rgba(255, 255, 255, 0.05);
  width: 100%;
}
.progress-fill {
  height: 100%;
  background: var(--accent-light);
  box-shadow: 0 0 10px var(--accent-glow);
  transition: width 0.5s cubic-bezier(0.34, 1.56, 0.64, 1);
}

/* ── TYPOGRAPHY & LAYOUT ───────────────────────────────── */
.step-container {
  padding: 2.5rem 2.5rem 3rem;
  display: flex;
  flex-direction: column;
}
.step-title {
  font-size: 1.6rem;
  font-weight: 800;
  color: var(--text-primary);
  margin: 0 0 0.5rem 0;
  letter-spacing: -0.01em;
  line-height: 1.2;
}
.step-sub {
  font-size: 0.95rem;
  color: var(--text-secondary);
  margin: 0 0 2rem 0;
  line-height: 1.5;
}

/* ── MULTIPLE CHOICE CARDS ─────────────────────────────── */
.options-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}
.options-grid.list-style {
  grid-template-columns: 1fr;
}

.option-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 1.5rem 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 1rem;
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.option-card:hover, .option-card:active {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.15);
  transform: translateY(-3px);
  box-shadow: 0 10px 24px rgba(0,0,0,0.2);
}
.option-icon {
  font-size: 1.8rem;
  height: 48px;
  display: flex;
  align-items: center;
}
.option-text {
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-primary);
  line-height: 1.3;
}

.option-card-row {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  padding: 1.25rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  transition: all 0.2s ease;
}
.option-card-row:hover, .option-card-row:active {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.15);
  padding-left: 2rem;
}
.option-card-row .arrow {
  color: var(--text-muted);
  transition: color 0.2s, transform 0.2s;
}
.option-card-row:hover .arrow {
  color: var(--accent-light);
  transform: translateX(4px);
}

/* ── FORMS ─────────────────────────────────────────────── */
.input-glass { 
  width: 100%; 
  padding: 1rem 1.25rem; 
  background: rgba(255, 255, 255, 0.03); 
  border: 1px solid rgba(255, 255, 255, 0.08); 
  border-radius: 16px; 
  color: var(--text-primary); 
  font-family: inherit; 
  outline: none; 
  transition: all 0.25s ease; 
  box-sizing: border-box; 
}
.input-glass::placeholder { color: var(--text-muted); opacity: 0.6; }
.input-glass:hover { border-color: rgba(255, 255, 255, 0.15); background: rgba(255, 255, 255, 0.05); }
.input-glass:focus { border-color: var(--accent-light); box-shadow: 0 0 0 3px var(--accent-glow); background: rgba(255, 255, 255, 0.06); }

.input-with-icon { position: relative; display: flex; align-items: center; }
.input-with-icon .icon { position: absolute; left: 1.25rem; color: var(--text-muted); font-size: 1.1rem; pointer-events: none; transition: color 0.3s ease; }
.input-glass.pl-10 { padding-left: 3.25rem; }
.input-with-icon:focus-within .icon { color: var(--accent-light); }

/* ── BUTTONS & UTILS ───────────────────────────────────── */
.dash-btn { display: inline-flex; align-items: center; gap: 8px; padding: 0.85rem 1.5rem; border: none; border-radius: 14px; font-weight: 700; cursor: pointer; transition: all 0.25s ease; font-family: inherit; }
.dash-btn:active { transform: scale(0.98); }
.btn-primary-solid { background: var(--text-primary); color: #000; box-shadow: 0 8px 24px rgba(255,255,255,0.1); }
.btn-primary-solid:hover:not(:disabled) { background: #fff; transform: translateY(-2px); box-shadow: 0 12px 32px rgba(255,255,255,0.2); }
.btn-primary-solid:disabled { opacity: 0.5; cursor: not-allowed; }
.btn-ghost { background: transparent; color: var(--text-muted); }
.btn-ghost:hover { color: var(--text-primary); }
.btn-ghost.border-visible { border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.05); color: var(--text-primary); }
.btn-ghost.border-visible:hover { background: rgba(255,255,255,0.1); }

.text-back-btn { background: none; border: none; color: var(--text-muted); font-size: 0.85rem; font-weight: 600; cursor: pointer; transition: color 0.2s; padding: 0; display: inline-flex; align-items: center; gap: 0.4rem; font-family: inherit; width: fit-content; }
.text-back-btn:hover { color: var(--text-primary); }

.privacy-note { font-size: 0.75rem; color: var(--text-muted); display: flex; align-items: center; justify-content: center; gap: 0.4rem; }
.success-icon { width: 64px; height: 64px; border-radius: 50%; background: rgba(16, 185, 129, 0.15); color: #10b981; font-size: 1.75rem; display: flex; align-items: center; justify-content: center; margin: 0 auto; box-shadow: 0 8px 24px rgba(16, 185, 129, 0.2); }

.w-full { width: 100%; } .mt-6 { margin-top: 1.5rem; } .mb-4 { margin-bottom: 1rem; } .py-6 { padding-top: 1.5rem; padding-bottom: 1.5rem; } .text-lg { font-size: 1.125rem; } .text-center { text-align: center; } .mx-auto { margin-left: auto; margin-right: auto;} .justify-center { justify-content: center;}

/* ── ANIMATIONS ────────────────────────────────────────── */
.slide-step-enter-active, .slide-step-leave-active { transition: all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1); }
.slide-step-enter-from { opacity: 0; transform: translateX(30px); }
.slide-step-leave-to { opacity: 0; transform: translateX(-30px); }

.animate-fade-in { animation: fadeIn 0.4s ease-out forwards; }
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

@media (max-width: 640px) {
  /* Added responsive padding to clear navbar on mobile too */
  .wizard-wrapper { padding: 6rem 1rem 3rem; }
  .step-container { padding: 2rem 1.5rem; }
  .step-title { font-size: 1.4rem; }
  .options-grid { grid-template-columns: 1fr; gap: 0.75rem; }
  .option-card { flex-direction: row; text-align: left; padding: 1.25rem 1rem; gap: 1.25rem; }
  .option-icon { height: auto; font-size: 1.5rem; }
}
</style>